import React, { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  ConfirmationDialog,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';
import { useToast } from '../../hooks/useToastContext';
import {
  performInterventionForVisit,
  completeInterventionForVisit,
  deleteInterventionFromVisit,
} from '../../services/api';
import type {
  PatientInterventionDetail,
  InterventionProgressResult,
  InterventionProgressDetail,
} from '../../services/api/types.gen';

const INTERVENTION_PROGRESS_RESULT_VALUES: InterventionProgressResult[] = [
  'returned_demonstration',
  'verbalized_understanding',
  'needs_reinforcement',
];

const RESULT_LABELS: Record<InterventionProgressResult, string> = {
  returned_demonstration: 'Returned demonstration',
  verbalized_understanding: 'Verbalized understanding',
  needs_reinforcement: 'Needs reinforcement',
};

interface PerformanceBoxProps {
  visitId: string;
  patientInterventionId: string;
  onCancel: () => void;
  onSaveSuccess: () => void;
  initialProgress?: InterventionProgressDetail;
}

const PerformanceBox: React.FC<PerformanceBoxProps> = ({
  visitId,
  patientInterventionId,
  onCancel,
  onSaveSuccess,
  initialProgress,
}) => {
  const { error: showError } = useToast();
  const [isPerformedOnPatient, setIsPerformedOnPatient] = useState(
    initialProgress?.is_performed_on_patient ?? false
  );
  const [isPerformedOnCaregiver, setIsPerformedOnCaregiver] = useState(
    initialProgress?.is_performed_on_caregiver ?? false
  );
  const [result, setResult] = useState<InterventionProgressResult | ''>(
    initialProgress?.result ?? ''
  );
  const [notes, setNotes] = useState(initialProgress?.performance_note ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = () => {
    if (!result) return;
    setIsSubmitting(true);
    performInterventionForVisit({
      path: {
        visit_id: visitId,
        patient_intervention_id: patientInterventionId,
      },
      body: {
        is_performed_on_patient: isPerformedOnPatient,
        is_performed_on_caregiver: isPerformedOnCaregiver,
        result: result as InterventionProgressResult,
        progress_note: notes || undefined,
      },
      throwOnError: false,
    })
      .then(() => {
        onSaveSuccess();
      })
      .catch(() => {
        showError('Failed to record intervention performance');
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div className='relative space-y-2 rounded border border-border bg-background p-2 pr-10 text-xs'>
      <button
        type='button'
        onClick={onCancel}
        className='absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-sm text-lg text-muted-foreground hover:bg-muted hover:text-foreground'
        aria-label='Close'
      >
        ×
      </button>
      <div>
        <div className='font-medium text-muted-foreground mb-1'>
          Performed on
        </div>
        <div className='flex gap-4'>
          <div className='flex items-center gap-2'>
            <Checkbox
              id={`patient-${patientInterventionId}`}
              checked={isPerformedOnPatient}
              onCheckedChange={checked =>
                setIsPerformedOnPatient(checked === true)
              }
              checkboxSize='sm'
            />
            <Label
              htmlFor={`patient-${patientInterventionId}`}
              className='cursor-pointer select-none'
            >
              Patient
            </Label>
          </div>
          <div className='flex items-center gap-2'>
            <Checkbox
              id={`caregiver-${patientInterventionId}`}
              checked={isPerformedOnCaregiver}
              onCheckedChange={checked =>
                setIsPerformedOnCaregiver(checked === true)
              }
              checkboxSize='sm'
            />
            <Label
              htmlFor={`caregiver-${patientInterventionId}`}
              className='cursor-pointer select-none'
            >
              Caregiver
            </Label>
          </div>
        </div>
      </div>
      <div>
        <Label
          htmlFor={`result-${patientInterventionId}`}
          className='text-muted-foreground'
        >
          Result
        </Label>
        <Select
          value={result}
          onValueChange={v => setResult(v as InterventionProgressResult)}
        >
          <SelectTrigger
            id={`result-${patientInterventionId}`}
            selectSize='sm'
            className='mt-1'
          >
            <SelectValue placeholder='Select result' />
          </SelectTrigger>
          <SelectContent>
            {INTERVENTION_PROGRESS_RESULT_VALUES.map(val => (
              <SelectItem key={val} value={val}>
                {RESULT_LABELS[val]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label
          htmlFor={`notes-${patientInterventionId}`}
          className='text-muted-foreground'
        >
          Notes (Optional)
        </Label>
        <Textarea
          id={`notes-${patientInterventionId}`}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder='Add notes...'
          textareaSize='sm'
          className='mt-1 min-h-[60px]'
        />
      </div>
      <div className='flex justify-end'>
        <Button
          size='sm'
          onClick={handleSubmit}
          disabled={
            !(isPerformedOnPatient || isPerformedOnCaregiver) ||
            !result ||
            isSubmitting
          }
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

interface DisplayInterventionCardProps {
  intervention: PatientInterventionDetail;
  visitId: string;
  isViewOnly?: boolean;
  /** Whether this visit type can record progress; see DisplayGoalCard. */
  canTrackProgress: boolean;
  onPerformanceSaved?: () => void;
}

const DisplayInterventionCard: React.FC<DisplayInterventionCardProps> = ({
  intervention,
  visitId,
  isViewOnly = false,
  canTrackProgress,
  onPerformanceSaved,
}) => {
  const { error: showError } = useToast();
  const [showPerformanceBox, setShowPerformanceBox] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canDelete =
    intervention.completed_on_visit_id == null &&
    (!intervention.progress_history ||
      intervention.progress_history.length === 0);

  const handleDeleteClick = () => {
    if (!canDelete || isDeleting) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    setIsDeleting(true);
    deleteInterventionFromVisit({
      path: {
        visit_id: visitId,
        patient_intervention_id: intervention.id,
      },
      throwOnError: false,
    })
      .then(() => {
        onPerformanceSaved?.();
      })
      .catch(() => {
        showError(
          'Failed to delete intervention',
          `Failed to delete intervention "${intervention.name}".`
        );
      })
      .finally(() => setIsDeleting(false));
  };

  const handleCompleteClick = () => {
    setShowCompleteConfirm(true);
  };

  const handleConfirmComplete = () => {
    setShowCompleteConfirm(false);
    setIsCompleting(true);
    completeInterventionForVisit({
      path: {
        visit_id: visitId,
        patient_intervention_id: intervention.id,
      },
      throwOnError: false,
    })
      .then(() => {
        onPerformanceSaved?.();
      })
      .catch(() => {
        showError(
          'Failed to complete intervention',
          `Failed to complete intervention "${intervention.name}".`
        );
      })
      .finally(() => setIsCompleting(false));
  };

  const currentProgress = intervention.progress_history?.find(
    p => p.visit_id === visitId
  );
  const hasPerformedProgress =
    intervention.progress_history?.some(p => p.is_performed) ?? false;
  const hasCompletedProgress = intervention.completed_on_visit_id != null;
  const formattedCompletedDate =
    intervention.completed_date != null
      ? new Date(intervention.completed_date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : null;

  return (
    <>
      <Card className='bg-muted/30 border-dashed w-full p-1.5 sm:p-1.5 md:p-1.5'>
        <CardContent className='p-2 space-y-1'>
          <div className='font-medium text-xs'>{intervention.name}</div>
          <p className='text-xs text-muted-foreground leading-relaxed'>
            {intervention.instruction}
          </p>
          <div className='flex flex-wrap gap-2 pt-1 min-h-[32px] items-center justify-start'>
            {hasCompletedProgress ? (
              <span className='text-xs text-muted-foreground'>
                completed on:{' '}
                <span className='font-semibold text-foreground'>
                  {formattedCompletedDate ?? '—'}
                </span>
              </span>
            ) : (
              <>
                {!isViewOnly && canTrackProgress ? (
                  <>
                    <Button
                      variant='outline'
                      size='sm'
                      disabled={!hasPerformedProgress || isCompleting}
                      onClick={handleCompleteClick}
                    >
                      Complete
                    </Button>
                    {currentProgress ? (
                      <div className='flex items-center gap-1'>
                        <span className='text-xs font-semibold text-muted-foreground tracking-wide'>
                          PERFORMED
                        </span>
                        <button
                          type='button'
                          onClick={() => setShowPerformanceBox(prev => !prev)}
                          className='flex items-center justify-center rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground'
                          aria-label='Edit performance'
                        >
                          <Pencil className='h-3 w-3' />
                        </button>
                      </div>
                    ) : (
                      <Button
                        variant={showPerformanceBox ? 'secondary' : 'outline'}
                        size='sm'
                        disabled={showPerformanceBox}
                        onClick={() =>
                          !showPerformanceBox && setShowPerformanceBox(true)
                        }
                      >
                        Perform
                      </Button>
                    )}
                  </>
                ) : (
                  currentProgress && (
                    <span className='text-xs font-semibold text-muted-foreground tracking-wide'>
                      PERFORMED
                    </span>
                  )
                )}
                {/* Deleting an intervention is not a progress action. It is
                    attached on the note that opens care and the backend leaves
                    the delete available there, so an accidental one stays
                    removable on a visit that cannot record progress. */}
                {!isViewOnly && canDelete && (
                  <button
                    type='button'
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                    className='flex shrink-0 items-center justify-center rounded p-1.5 text-destructive hover:bg-destructive/10 disabled:opacity-50'
                    aria-label='Delete intervention'
                  >
                    <Trash2 className='h-5 w-5' />
                  </button>
                )}
              </>
            )}
          </div>
          {showPerformanceBox && !isViewOnly && canTrackProgress && (
            <div className='pt-2'>
              <PerformanceBox
                visitId={visitId}
                patientInterventionId={intervention.id}
                onCancel={() => setShowPerformanceBox(false)}
                onSaveSuccess={() => {
                  setShowPerformanceBox(false);
                  onPerformanceSaved?.();
                }}
                initialProgress={currentProgress}
              />
            </div>
          )}
        </CardContent>
      </Card>
      <ConfirmationDialog
        open={showCompleteConfirm}
        title='Complete intervention?'
        message='This intervention will be inactivated. It can no longer have progress tracked and can no longer be discontinued.'
        confirmLabel='Complete'
        onAccept={handleConfirmComplete}
        onCancel={() => setShowCompleteConfirm(false)}
      />
      <ConfirmationDialog
        open={showDeleteConfirm}
        title='Delete intervention?'
        message='You are about to permanently delete this intervention. This action cannot be undone.'
        confirmLabel='Delete'
        onAccept={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  );
};

export default DisplayInterventionCard;
